import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import type { Metrics } from "@/types/dashboard";

interface ScoreDistributionProps {
  metrics: Metrics | null;
  tasks?: Record<string, any>;
}

// Custom tooltip with glassmorphism
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    // Skip spacer bars
    if (data.isSpacer) return null;

    const score = payload[0].value;
    return (
      <div className="glass-tooltip">
        <p className="font-semibold text-amber-200">{data.fullLabel}</p>
        <p className="text-lg font-bold bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
          Score: {score?.toFixed(1) ?? 'N/A'}/5.0
        </p>
      </div>
    );
  }
  return null;
};

// Parse question_id format: q001_run1, q002_run2, etc.
interface ParsedQuestionId {
  questionNum: number;
  runNum: number;
  original: string;
}

function parseQuestionId(questionId: string): ParsedQuestionId | null {
  // Match patterns like: q001_run5, q001_finance_run5, q002_hr_run3, etc.
  const match = questionId.match(/q(\d+)(?:_\w+)?_run(\d+)/);
  if (!match) return null;
  return {
    questionNum: parseInt(match[1], 10),
    runNum: parseInt(match[2], 10),
    original: questionId
  };
}

export function ScoreDistribution({ metrics, tasks }: ScoreDistributionProps) {
  const chartData = useMemo(() => {
    if (!metrics || !tasks) {
      return [];
    }

    // Extract all completed tasks with scores
    const taskList = Object.entries(tasks)
      .filter(([_, task]: [string, any]) => (task.status === 'completed' || task.status === 'success') && task.accuracy_score !== undefined && task.accuracy_score !== null)
      .map(([questionId, task]: [string, any]) => {
        const parsed = parseQuestionId(questionId);
        return parsed ? {
          ...parsed,
          score: task.accuracy_score,
          questionId
        } : null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // If no completed tasks with scores, return empty
    if (taskList.length === 0) {
      return [];
    }

    // Sort by question number first, then by run number
    taskList.sort((a, b) => {
      if (a.questionNum !== b.questionNum) {
        return a.questionNum - b.questionNum;
      }
      return a.runNum - b.runNum;
    });

    // Group by question number and add spacers
    const chartData: any[] = [];
    let currentQuestion = -1;

    taskList.forEach((item) => {
      // Add spacer when moving to a new question (except for first question)
      if (item.questionNum !== currentQuestion) {
        if (currentQuestion !== -1) {
          // Add a small spacer bar between question groups
          chartData.push({
            label: '',
            score: 0,
            isSpacer: true,
            fullLabel: ''
          });
        }
        currentQuestion = item.questionNum;
      }

      // Short label: "1.2" means Q1 Run2
      chartData.push({
        label: `${item.questionNum}.${item.runNum}`,
        score: item.score,
        isSpacer: false,
        fullLabel: `Q${item.questionNum} R${item.runNum}`,
        questionNum: item.questionNum,
        runNum: item.runNum
      });
    });

    return chartData;
  }, [metrics, tasks]);

  const getBarColor = (score: number, isSpacer: boolean) => {
    if (isSpacer) return "transparent"; // Invisible spacer
    if (score >= 4.5) return "#22c55e"; // Green
    if (score >= 3.5) return "#eab308"; // Yellow
    if (score >= 2.5) return "#f97316"; // Orange
    return "#ef4444"; // Red
  };

  if (!metrics || chartData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Waiting for evaluation data...
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col">
      <div className="mb-3">
        <h3 className="text-base font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
          All Scores by Question & Run
        </h3>
        <p className="text-xs text-muted-foreground">Format: Q.R (e.g., 1.2 = Question 1, Run 2)</p>
      </div>

      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(168, 139, 250, 0.1)" />
            <XAxis
              dataKey="label"
              tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 9 }}
              stroke="rgba(168, 139, 250, 0.3)"
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              domain={[0, 5]}
              ticks={[0, 1, 2, 3, 4, 5]}
              tick={{ fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11 }}
              stroke="rgba(168, 139, 250, 0.3)"
              label={{
                value: 'Score',
                angle: -90,
                position: 'insideLeft',
                style: { fill: 'rgba(255, 255, 255, 0.6)', fontSize: 11 }
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(168, 139, 250, 0.1)' }} />
            <Bar dataKey="score" radius={[6, 6, 0, 0]} maxBarSize={30}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.score, entry.isSpacer)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
